import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';
import { filter, switchMap } from 'rxjs';

import { jsonDefaultRequestOptions } from 'src/app/shared/constants/http.constants';
import { handleHttpResponse } from 'src/app/shared/functions/http.functions';
import { baseEventsUrl } from 'src/app/shared/services/events/events.service';
import {
  BaseScheduleAddition,
  GetScheduleAdditionsPayload,
  GetScheduleAdditionsResponse,
  ScheduleAddition,
  UpdateScheduleAdditionPayload
} from 'src/app/shared/services/events/schedule-additions/schedule-additions.models';
import { AuthenticationStore } from 'src/app/shared/state/authentication/authentication.state';

const baseScheduleAdditionsUrl = (eventId: string) =>
  `${baseEventsUrl}/${eventId}/scheduleAdditions`;
const createScheduleAdditionUrl = (eventId: string) =>
  `${baseScheduleAdditionsUrl(eventId)}/create`;
const deleteScheduleAdditionUrl = (
  eventId: string,
  scheduleAdditionId: string
) => `${baseScheduleAdditionsUrl(eventId)}/${scheduleAdditionId}`;

@Injectable({
  providedIn: 'root'
})
export class ScheduleAdditionService {
  private _auth0Service = inject(AuthService);
  private _httpClient = inject(HttpClient);
  private _authStore = inject(AuthenticationStore);

  getScheduleAdditions$(
    eventId: string,
    payload: GetScheduleAdditionsPayload,
    page: number,
    size: number
  ) {
    return this._auth0Service.isAuthenticated$.pipe(
      filter((authenticated) => authenticated),
      switchMap(() =>
        handleHttpResponse(
          this._httpClient.post<GetScheduleAdditionsResponse>(
            baseScheduleAdditionsUrl(eventId),
            payload,
            {
              ...jsonDefaultRequestOptions,
              params: { size, page }
            }
          ),
          'getScheduleAdditions$',
          this._authStore.logout
        )
      )
    );
  }

  createScheduleAddition$(eventId: string, payload: BaseScheduleAddition) {
    return this._auth0Service.isAuthenticated$.pipe(
      filter((authenticated) => authenticated),
      switchMap(() =>
        handleHttpResponse(
          this._httpClient.post<ScheduleAddition>(
            createScheduleAdditionUrl(eventId),
            payload
          ),
          'createScheduleAddition$',
          this._authStore.logout
        )
      )
    );
  }

  updateScheduleAddition$(
    eventId: string,
    payload: UpdateScheduleAdditionPayload
  ) {
    return this._auth0Service.isAuthenticated$.pipe(
      filter((authenticated) => authenticated),
      switchMap(() =>
        handleHttpResponse(
          this._httpClient.patch<ScheduleAddition>(
            baseScheduleAdditionsUrl(eventId),
            payload
          ),
          'updateScheduleAddition$',
          this._authStore.logout
        )
      )
    );
  }

  deleteScheduleAddition$(eventId: string, scheduleAdditionId: string) {
    return this._auth0Service.isAuthenticated$.pipe(
      filter((authenticated) => authenticated),
      switchMap(() =>
        handleHttpResponse(
          this._httpClient.delete<void>(
            deleteScheduleAdditionUrl(eventId, scheduleAdditionId)
          ),
          'deleteScheduleAddition$',
          this._authStore.logout
        )
      )
    );
  }
}
